/**
 * First we will load all of this project's JavaScript dependencies which
 * include Vue and Vue Resource. This gives a great starting point for
 * building robust, powerful web applications using Vue and Laravel.
 */

import './bootstrap'

// External Libraries
import Vue from 'vue'
import { idleConfig, ionSound, tooltips, helpers, fontawesome } from './utils'

// Internal Modules
import store from './store'
import { handleUpdatedUserStatus, postStatusToChannel } from './channels/user-presence-channel'

// Event listeners
import { startVisitListener, endVisitListener } from './listeners/visit-event'
import { chatEventListener } from './listeners/chat-event'
import { notificationEventListener } from './listeners/notification-event'

// Sentry and its integrations
import * as Sentry from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';
import { CaptureConsole } from '@sentry/integrations';

const sentryConfig = window.env.sentry.backpack_vue;

// Initialize Sentry
Sentry.init({
    Vue,
    dsn: sentryConfig.DSN,
    environment: sentryConfig.env,
    integrations: [
        new BrowserTracing({
            tracePropagationTargets: [sentryConfig.domain, /^\//],
        }),
        new CaptureConsole({
            levels: ['error'],
        })
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    release: sentryConfig.release
})

// Module Initialization
helpers.updateIconsUsersLists()
tooltips.initializeTooltips()
ionSound.initializeIonSound()
fontawesome.initFontAwesome()

// Status Change Callback
const statusChangeCallback = (newStatus) => {
    const currentStatus = store.state.activity.activity.status;

    // Dispatch setUserActivity only if new activity differs
    if (newStatus !== currentStatus) {
        store.dispatch('activity/setCurrentActivity', [newStatus, null])
    }
}

/**
 * Next, we will create a fresh Vue application instance and attach it to
 * the body of the page. From here, you may begin adding components to
 * the application, or feel free to tweak this setup for your needs.
 */
if (typeof id !== 'undefined') {
    new Vue({
        el: '#app-user-activity',
        store,
        components: {
            // Load activity dropdown component
            "activity": require('./layouts/header/UserActivity.vue').default
        }
    })

    // Idle Configuration and Initialization
    const idleTime = idle_time // From backpack (extras.blade file)
    const idleConf = idleConfig.getIdleConfig(idleTime, statusChangeCallback)
    const idle = idleConfig.initializeIdle(idleConf).start() // Start the IdleJs instance

    idleConf.onActive(); // Trigger onActive after initializing idle configuration

    new Vue({
        el: '#app-overlay',
        store,
        components: {
            // Load overlay components
            "overlay-fireworks-sales": require('./components/overlay/OverlayFireworksSales.vue').default,
            "overlay-fireworks-visa": require('./components/overlay/OverlayFireworksVisa.vue').default,
            "overlay-incoming-call": require('./components/overlay/OverlayIncomingCall.vue').default,
            "overlay-incoming-video-call": require('./components/overlay/OverlayIncomingVideoCall.vue').default,
            "overlay-scheduled-video-call": require('./components/overlay/OverlayScheduledVideoCall.vue').default,
            "overlay-dnd": require('./components/overlay/OverlayDND.vue').default,
            "overlay-check-status": require('./components/overlay/OverlayCheckStatus.vue').default,
            "overlay-course-ongoing": require('./components/overlay/OverlayCourseOngoing.vue').default,
            "overlay-course-overdue": require('./components/overlay/OverlayCourseOverdue.vue').default,
            "overlay-school-feedback": require('./components/overlay/OverlaySchoolFeedback.vue').default
        },
        data: {
            courseUrl: window.courseUrl
        }
    })

    new Vue({
        el: '#app-users-list',
        store,
        components: {
            "users-list-container": require('./layouts/sidebar/right-sidebar/UsersListContainer.vue').default
        }
    })

    if (document.getElementById('app-uni-chat-page')) {
        new Vue({
            el: '#app-uni-chat-page',
            store,
            components: {
                "chat-page": require('./features/uni-chat/pages/index.vue').default
            }
        })
    }

    new Vue({
        el: '#app-uni-chat',
        store,
        components: {
            "chat-container": require('./features/uni-chat/ChatContainer.vue').default,
            "chat-conversation-list": require('./features/uni-chat/ChatConversationList.vue').default
        }
    })

    if (document.getElementById('app-commissions')) {
        new Vue({
            el: '#app-commissions',
            store,
            components: {
                "payment-settings": require('./features/commissions/PaymentSettings.vue').default,
                "commissions": require('./features/commissions/Commissions.vue').default,
                "closed-students-navigation": require('./features/commissions/components/ClosedStudentsNavigation.vue').default,
                "closed-students-table": require('./features/commissions/components/ClosedStudentsTable.vue').default
            }
        })
    }

    if (document.getElementById('app-reply-with-ai')) {
        new Vue({
            el: '#app-reply-with-ai',
            store,
            components: {
                "reply-ai": require('./features/tickets/compopnents/WriteWithAI.vue').default,
            }
        })
    }

    if (document.getElementById('app-target-goals')) {
        new Vue({
            el: '#app-target-goals',
            components: {
                "goals-section": require('./features/goals/GoalsSection.vue').default,
                "role-models-section": require('./features/user-role-models/RoleModelSection.vue').default,
            }
        })
    }

    if (document.getElementById('app-target-goals')) {
        new Vue({
            el: '#app-sales-rank',
            components: {
                "sales-rank-section": require('./features/goals/SalesRankSection.vue').default
            }
        })
    }

    if (document.getElementById('app-setup-goals')) {
        new Vue({
            el: '#app-setup-goals',
            store,
            components: {
                "setup-goals-button": require('./features/goals/components/SetupGoalsButton.vue').default
            }
        })
    }

    if (document.getElementById('app-todo')) {
        new Vue({
            el: '#app-todo',
            store,
            components: {
                "to-do": require('./features/to-do/ToDo.vue').default
            }
        })
    }

    new Vue({
        el: '#app',
        created() {
            // Check if 'id' is defined before using it
            if (typeof id !== 'undefined') {
                const currentStatus = store.state.activity.activity.status

                startVisitListener(id)
                endVisitListener(id)
                chatEventListener(id, idle)
                notificationEventListener(id)

                /* Send status to other tabs when the tab gains focus */
                window.onfocus = () => {
                    postStatusToChannel(id, currentStatus)
                }
                /**/

                /* Handle updated user status from broadcast channel for inactive tabs */
                handleUpdatedUserStatus(id, idle)
            }
        }
    });

    if (document.getElementById('app-level-points-rank')) {
        new Vue({
            el: '#app-level-points-rank',
            components: {
                "level-points-rank": require('./features/user-rank/RankByLevelPoints.vue').default
            }
        })
    }
}

$('#incoming-call-close').click(function (e) {
    e.preventDefault();
    $('.overlay-incoming-call').hide()
    helpers.hideOverlay('overlay-incoming-call')
});

$('#incoming-video-call-button').click(function (e) {
    $('.overlay-incoming-video-call').hide();
    helpers.hideOverlay('overlay-incoming-video-call')
});

$('#incoming-video-call-close').click(function (e) {
    e.preventDefault();
    $('.overlay-incoming-video-call').hide();
    helpers.hideOverlay('overlay-incoming-video-call');
});

$('#scheduled-video-call-button').click(function (e) {
    e.preventDefault();
    $('.overlay-scheduled-video-call').hide();
    helpers.hideOverlay('overlay-scheduled-video-call');
})

$('#scheduled-video-profile-button').click(function (e) {
    e.preventDefault();
    $('.overlay-scheduled-video-call').hide();
    helpers.hideOverlay('overlay-scheduled-video-call');
})

$('#scheduled-video-call-close').click(function (e) {
    e.preventDefault();
    $('.overlay-scheduled-video-call').hide();
    helpers.hideOverlay('overlay-scheduled-video-call');
})

$(document).on('click', "#users-toggle", function (e) {
    e.preventDefault();
    helpers.updateIconsUsersLists();
})

$(document).on('click', '.video-call-coach', function (e) {
    e.preventDefault();
    let modal = $('#chatModal')
    let button = $(this)
    let user_id = button.data('user-id')
    modal.find('.modal-content').empty();
    modal.find('.modal-content').load('/admin/coach/' + user_id + '/video-call', function (result) {
        modal.modal({show: true});
    });
})

$(document).on('click', '.call-coach', function (e) {
    e.preventDefault();
    let modal = $('#chatModal')
    let button = $(this)
    let user_id = button.data('user-id')
    modal.find('.modal-content').empty();
    modal.find('.modal-content').load('/admin/coach/' + user_id + '/call', function (result) {
        modal.modal({show: true});
    });
})

$(document).on('click', '.preview-btn', function (e) {
    e.preventDefault();
    const firework_level = $(this).data('level');
    const firework_message = $(this).data('message');

    $.ajax({
        url: '/admin/fireworks/launch-preview',
        type: 'GET',
        data: {
            level: firework_level,
            message: firework_message
        },
        success: function (data) {
            if (data.success) {
                if (data.level === 'platinum') {
                    ion.sound.play('fireworks_sales_platinum');
                } else if (data.level === 'silver' || data.level === 'gold') {
                    ion.sound.play('applause');
                } else {
                    ion.sound.play('fireworks');
                }

                // Emit an event to show the fireworks
                helpers.eventBus.$emit('show-fireworks', data.level, data.student_name, data.name, data.image, data.message);
            } else {
                alert('Something went wrong.');
            }
        },
        error: function () {
            alert('An error occurred.');
        }
    });
});

$('#school-feedback-close').click(function (e) {
    e.preventDefault();
    $('.overlay-school-feedback').hide()
    helpers.hideOverlay('overlay-school-feedback')
});

$('#currentCommissionsModal').on('shown.bs.modal', function () {
    if (document.getElementById('app-commissions')) {
        new Vue({
            el: '#app-commissions',
            store,
            components: {
                "current-commissions-table": require('./features/commissions/components/CurrentCommissionsTable.vue').default
            }
        });
    }
});

$(document).ready(function () {
    // Reinitialize CoreUI dropdown behavior
    $('.nav-dropdown-toggle').click(function (e) {
        e.preventDefault();

        const dropdown = $(this).closest('.nav-item.nav-dropdown');
        dropdown.toggleClass('open');
    });

    $('.aside-menu-toggler').click(function (e) {
        e.preventDefault();

        $('body').toggleClass('aside-menu-xl-show');
    });

    $('.sidebar-toggler').click(function (e) {
        e.preventDefault();

        $('body').toggleClass('sidebar-lg-show');
    });

    helpers.adjustDropdownPosition();
});

// Add more filter types that use select options here
const select2FilterTypes = new Set(['dropdown', 'select2', 'select2_multiple', 'select2_ajax', 'select2_from_array', 'select2_from_ajax']);

// Ensure Select2 is initialized before dropdown toggles
$(document).on('click', '.dropdown-toggle, .nav-link[data-toggle="dropdown"]', function (e) {
    e.preventDefault();

    const dropdown = $(this).closest('li');
    const select = dropdown.find('select');
    const filterType = dropdown.attr('filter-type');
    const targetDropdown = $(this).next('.dropdown-menu, .dropdown-content');

    if (select2FilterTypes.has(filterType) && !select.data('select2')) {
        select.select2();
    }

    if (!targetDropdown.hasClass('show')) {
        $(this).dropdown('toggle');
    }

    // Add a short delay for proper toggling
    setTimeout(() => {
        if (!targetDropdown.hasClass('show')) {
            $(this).dropdown('toggle');
        }

        helpers.adjustDropdownPosition();
    }, 50);
});